import type {
  ProductModifierValue,
  SelectedElementDesignation,
} from "schemas/generated/fileModifiers";

import React from "react";

import { Loader } from "@editor/components/common/Loader";
import useCurrentProjectId from "@editor/hooks/useCurrentProjectId";
import { useProductList } from "@editor/hooks/useProductList";
import { productRouter } from "@editor/trpc/product";

import { Combobox } from "@replo/design-system/components/combobox/Combobox";
import { LargeMenuItem } from "@replo/design-system/components/menu/LargeMenuItem";
import { Tag } from "@replo/design-system/components/tag/Tag";
import { skipToken } from "@tanstack/react-query";
import startCase from "lodash-es/startCase";
import { ChevronDown, ImageIcon, SearchIcon } from "lucide-react";
import { useOverridableState } from "replo-runtime/shared/hooks/useOverridableState";
import { exhaustiveSwitch } from "replo-utils/lib/misc";
import { useDebouncedState } from "replo-utils/react/use-debounced-state";

import { useSeletedElementDesignations } from "../store/ModifiersStoreContext";

function isProductDesignation(
  designation: SelectedElementDesignation,
): designation is SelectedElementDesignation & {
  type: "dynamicDataSource";
  data: { type: "product" };
} {
  return (
    designation.type === "dynamicDataSource" &&
    designation.data.type === "product"
  );
}

const PRODUCTS_LIMIT = 100;

const getProductStatusVariant = (
  status: string,
): "success" | "info" | "neutral" => {
  return exhaustiveSwitch(
    { status },
    "status",
  )({
    active: "success",
    draft: "info",
    archived: "neutral",
  });
};

interface ProductSelectorModifierProps {
  onChange: (modifiers: ProductModifierValue[]) => void;
}

const ProductSelectorModifier: React.FC<ProductSelectorModifierProps> = ({
  onChange,
}) => {
  const designations = useSeletedElementDesignations();
  const productDesignation = designations?.find((designation) =>
    isProductDesignation(designation),
  );
  const projectId = useCurrentProjectId();
  const [isOpen, setIsOpen] = React.useState(false);
  const [searchTermInput, searchTerm, setSearchTerm] = useDebouncedState("");

  const [currentProductId, setCurrentProductId] = useOverridableState(
    productDesignation?.data.productId ?? "",
    React.useCallback(
      (productId: string) => {
        const modifiers: ProductModifierValue[] = [
          { type: "product", value: productId },
        ];
        onChange(modifiers);
      },
      [onChange],
    ),
  );

  const {
    products,
    isLoading,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useProductList({
    projectId,
    limit: PRODUCTS_LIMIT,
    search: searchTerm,
  });
  const {
    data: currentProduct,
    isLoading: isLoadingCurrentProduct,
    isFetching: isFetchingCurrentProduct,
  } = productRouter.get.useQuery(
    currentProductId ? { type: "id", id: currentProductId } : skipToken,
  );

  const options = React.useMemo(() => {
    return products.map((product) => {
      return {
        value: product.id,
        label: (
          <LargeMenuItem
            variant="product"
            label={product.title}
            description={product.slug ?? ""}
            productImage={product.featuredMedia?.url}
            tag={
              <Tag size="sm" variant={getProductStatusVariant(product.status)}>
                {startCase(product.status)}
              </Tag>
            }
            selected={currentProductId === product.id}
            tooltipBehavior="always"
            tooltip={
              <div className="flex w-[250px] flex-col gap-1">
                <p className="font-medium">{product.title}</p>
                <p>Handle: {product.slug}</p>
                <p>ID: {product.id}</p>
              </div>
            }
          />
        ),
        displayValue: product.title,
        estimatedSize: 48,
      };
    });
  }, [products, currentProductId]);

  const handleChange = (value: string) => {
    if (value) {
      setCurrentProductId(value);
    }
  };

  const handleSearch = (input: string) => {
    setSearchTerm(input);
  };

  const loadMoreProducts = async () => {
    if (hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  };

  const shouldShowLoadingIndicator =
    searchTermInput.trim().length === 0 &&
    (isLoadingCurrentProduct || isFetchingCurrentProduct);

  return (
    <Combobox.Root
      options={options}
      open={isOpen}
      onOpenChange={setIsOpen}
      value={currentProductId}
      onChange={handleChange}
      layoutClassName="w-full flex-1"
      syncInputWithSelectedValue={false}
    >
      <Combobox.Trigger>
        <button
          type="button"
          className="flex items-center justify-between w-full p-2 bg-white hover:bg-surface border border-border rounded-md text-left"
        >
          <div className="flex items-center gap-3 flex-1 min-w-0">
            <div className="flex-shrink-0">
              {currentProduct?.featuredMedia?.url ? (
                <div className="bg-surface h-8 w-8 flex items-center justify-center rounded overflow-hidden">
                  <img
                    src={currentProduct.featuredMedia.url}
                    alt={currentProduct.title}
                    className="h-full w-full object-cover"
                  />
                </div>
              ) : (
                <div className="bg-surface h-8 w-8 flex items-center justify-center rounded">
                  <ImageIcon className="text-muted size-4" />
                </div>
              )}
            </div>

            <div className="flex-1 min-w-0">
              {currentProduct ? (
                <>
                  <div className="text-sm font-medium text-default truncate">
                    {currentProduct.title}
                  </div>
                  <div className="flex items-center gap-2 mt-1">
                    <Tag
                      size="sm"
                      variant={getProductStatusVariant(currentProduct.status)}
                    >
                      {startCase(currentProduct.status)}
                    </Tag>
                    <span className="text-xs text-muted truncate">
                      {currentProduct.slug}
                    </span>
                  </div>
                </>
              ) : (
                <span className="text-sm text-muted">Select a product...</span>
              )}
            </div>
          </div>

          <div className="flex items-center gap-2 ml-2">
            {shouldShowLoadingIndicator && (
              <Loader className="size-4 text-muted" />
            )}
            <ChevronDown className="flex-shrink-0 size-4 text-muted" />
          </div>
        </button>
      </Combobox.Trigger>
      <Combobox.Popover
        side="bottom"
        sideOffset={4}
        layoutClassName="w-[260px] h-[400px]"
        align="start"
      >
        <Combobox.Content
          title="Select Product"
          inputPlaceholder="Search products..."
          emptySearchMessage="No products found."
          onScrolledToEnd={
            hasNextPage ? () => void loadMoreProducts() : undefined
          }
          loader={(() => {
            if (isFetchingNextPage || isLoading) {
              return (
                <div className="flex h-6 items-center justify-center">
                  <Loader className="size-6" />
                </div>
              );
            }
            if (hasNextPage) {
              return <div className="h-6" />;
            }
            return null;
          })()}
        >
          <Combobox.Input
            startEnhancer={<SearchIcon size={16} />}
            placeholder="Search products..."
            size="sm"
            controlledInput={searchTermInput}
            controlledOnInputChange={handleSearch}
          />
        </Combobox.Content>
      </Combobox.Popover>
    </Combobox.Root>
  );
};

export default ProductSelectorModifier;
